/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Threshold } from "@visx/threshold";
import dayjs from "dayjs";
import { Else, If, Then, When } from "react-if";
import { BrickLoader, Text, theme } from "@nordcloud/gnui";
import {
  ChartDataDisplayWrapper,
  Legend,
  LoaderWrap,
  anomaliesChartColors,
  getTooltipDate,
  getNumberOfTicks,
  ChartType,
  Anomaly,
  CostAnalysisFields,
  Granularity,
  Period,
  BarStackRenderer,
  calculateMaxDomain,
  calculateMinDomain,
  getInnerBarPadding,
  getMinMax,
  getOuterBarPadding,
} from "~/components";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { periodDayJs } from "~/services/customers";
import {
  formatMoney,
  formatMoneyLabel,
  formatMoneyWithoutSymbol,
  generateNumericArray,
  getLastItem,
  isEmpty,
  noop,
} from "~/tools";

const NO_DATA_MESSAGE =
  "The anomaly cost is not available yet. It usually takes a few days to recalculate.";

const NOT_PRECISE_DATA =
  "Cost data for this day may be not complete. Precise cost analysis estimation is not yet available.";

type Props = {
  height: number;
  width: number;
  anomalies: Anomaly[];
  startDate: string;
  endDate: string;
  granularity: Granularity;
  loading: boolean;
  period: Period;
  showTooltip: (args: TooltipArguments) => void;
  hideTooltip: () => void;
  handleDetailsClick: (date: string, anomalyCost: string) => void;
  containerRef?: (element: HTMLElement | SVGElement | null) => void;
};

type Item = {
  [key: string]: string;
};

export function AnomaliesChart({
  height,
  width,
  anomalies,
  startDate,
  endDate,
  granularity,
  loading,
  period,
  showTooltip,
  hideTooltip,
  handleDetailsClick,
  containerRef,
}: Props) {
  const { currency } = useCurrency();

  const xMax = width - verticalMargin;
  const yMax = height - verticalMargin;

  const isNotMonthsGranularity = granularity !== Granularity.MONTHS;

  // Calculate number of ticks on X axis
  const endDateDayjs = getNumberOfTicks(endDate, granularity);

  const xTicks = Math.ceil(endDateDayjs.diff(startDate, granularity, true));

  // Prepare array with all dates in range
  const xDates = useMemo(
    () =>
      generateNumericArray(xTicks).map((index) =>
        dayjs(startDate)
          .add(index, granularity)
          .startOf(granularity)
          .format(dateFormat.shortDate)
      ),
    [startDate, granularity, xTicks]
  );

  const yData = useMemo(
    () =>
      xDates.map((date) => {
        const value = Number(
          anomalies
            .find((timePoint) => timePoint.date === date)
            ?.cost.toFixed(2) ?? 0
        );
        return {
          date,
          cost: Number(formatMoneyWithoutSymbol(value)),
        };
      }),
    [xDates, anomalies]
  );

  const flatData = yData.flatMap((item) => item.cost);

  const hasNoData =
    isEmpty(anomalies) || yData.every(({ cost }) => cost < 0.01);

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        round: true,
        domain: [
          calculateMinDomain(flatData, hasNoData),
          calculateMaxDomain(flatData, hasNoData),
        ],
        nice: true,
      }),
    [yMax, hasNoData, flatData]
  );

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        range: [1, xMax],
        domain: xDates?.map((_, index) => index),
        padding: 0.1,
      }),
    [xMax, xDates]
  );

  const ticks = useMemo(
    () =>
      width > xTicksBreakpoint && period === Period.MONTH
        ? periodDayJs().daysInMonth()
        : periodDayJs().daysInMonth() / 3,
    [width, period]
  );

  const showTooltipElement = (event: React.MouseEvent, index: number) => {
    const anomalyDate = dayjs(xDates[index]);
    const tooltipDisplayDate = getTooltipDate(
      xDates[index],
      period,
      granularity
    );

    const selectedAnomaly = anomalies.find(
      (anomaly) => anomaly.date === xDates[index]
    );

    const anomalyCost = selectedAnomaly?.anomalyCost;

    const anomalyCostToDisplay = formatMoney(anomalyCost, currency);

    const isRecent = dayjs(tooltipDisplayDate).isAfter(
      dayjs().subtract(3, "days")
    );

    const coords = localPoint(event, event);

    showTooltip({
      tooltipLeft: coords?.x ?? event.clientX,
      tooltipTop: (coords?.y ?? event.clientY) - window.scrollY,
      tooltipData: (
        <div css={{ minWidth: "100px" }}>
          <Text m={0} size="xs" color={theme.color.text.text03}>
            {tooltipDisplayDate}
          </Text>
          <If condition={selectedAnomaly?.isAttached || isRecent}>
            <Then>
              <Text m={0} size="xs" color={theme.color.text.text04}>
                {NOT_PRECISE_DATA}
              </Text>
            </Then>
            <Else>
              <Text m={0} color={theme.color.text.text04} size="sm">
                Cost: {formatMoney(yData[index].cost, currency)}
              </Text>
              <When condition={anomalyCost}>
                <Text m={0} color={theme.color.text.text04} size="sm">
                  Anomaly Cost: {anomalyCostToDisplay}
                </Text>
              </When>
              <When condition={anomalyCost && isNotMonthsGranularity}>
                <Text
                  m={0}
                  color={theme.color.text.info}
                  size="sm"
                  css={{ cursor: "pointer" }}
                  onClick={() =>
                    handleDetailsClick(
                      dayjs(anomalyDate).format(dateFormat.shortDate),
                      anomalyCostToDisplay
                    )
                  }
                >
                  Check Details
                </Text>
              </When>
            </Else>
          </If>
        </div>
      ),
    });
  };
  const getDate = (item: Item) => xDates.indexOf(item.date);

  const padding = getInnerBarPadding(xDates.length, width);
  xScale.paddingInner(padding);
  xScale.paddingOuter(getOuterBarPadding(xDates.length));
  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 6 1"
      backgroundColor={theme.color.background.ui01}
    >
      <ChartDataDisplayWrapper
        hasData={!hasNoData}
        messageComponent={NO_DATA_MESSAGE}
      >
        <svg
          ref={containerRef}
          width={width}
          height={height}
          onMouseEnter={hideTooltip}
        >
          <Group top={VERTICAL_MARGIN / 2} left={40}>
            <GridRows
              scale={yScale}
              width={xMax}
              strokeDasharray="1,3"
              stroke={theme.color.border.focus}
              pointerEvents="none"
              numTicks={5}
            />
            <AxisLeft
              hideTicks
              top={0}
              scale={yScale}
              stroke={theme.color.border.focus}
              numTicks={5}
              tickFormat={(n) => formatMoneyLabel(n, currency)}
              tickLabelProps={() => ({
                fill: theme.color.text.text02,
                fontSize: 10,
                textAnchor: "end",
              })}
            />
            <Group>
              <AxisBottom
                top={yMax}
                scale={xScale}
                stroke={theme.color.border.focus}
                tickStroke={theme.color.border.focus}
                numTicks={ticks}
                tickFormat={(index) =>
                  granularity === "days"
                    ? dayjs(xDates[index]).format(dateFormat.dayMonthShort)
                    : dayjs(xDates[index]).format(
                        dateFormat.monthYearShortNoComma
                      )
                }
                tickLabelProps={() => ({
                  fill: theme.color.text.text02,
                  fontSize: 10,
                  textAnchor: "middle",
                })}
              />
            </Group>
            <When condition={isNotMonthsGranularity}>
              <Threshold
                id="anomaly-threshold-band"
                x={({ index }) => xScaleValue(xScale(index))}
                y0={({ upper }: { upper: number }) => yScale(upper) ?? 0}
                y1={({ lower }: { lower: number }) => yScale(lower) ?? 0}
                clipAboveTo={0}
                data={anomalies.map(
                  ({ upperCostEstimate, lowerCostEstimate }, index) => ({
                    upper: upperCostEstimate ?? 0,
                    lower: lowerCostEstimate ?? 0,
                    index,
                  })
                )}
                clipBelowTo={getLastItem(
                  getMinMax(anomalies.map(({ cost }) => cost))
                )}
                belowAreaProps={{
                  fill: anomaliesChartColors.threshold,
                }}
                aboveAreaProps={{
                  fill: anomaliesChartColors.threshold,
                }}
              />
            </When>
            <BarStackRenderer
              keys={[CostAnalysisFields.ANOMALY_COST]}
              data={yData ?? []}
              yScale={yScale}
              xScale={xScale}
              x={getDate}
              chartType={ChartType.ANOMALY_COST}
              colors={anomaliesChartColors}
              anomalies={anomalies}
              onMouseLeave={noop}
              onMouseOver={(event, index) => {
                showTooltipElement(event, index);
              }}
            />
          </Group>
        </svg>
        <ChartLegend granularity={granularity} />
      </ChartDataDisplayWrapper>
    </LoaderWrap>
  );
}

const VERTICAL_MARGIN = 40;
const verticalMargin = 40;
const xTicksBreakpoint = 1000;

type LegendProps = {
  granularity: Granularity;
};

function ChartLegend({ granularity }: LegendProps) {
  const costData = [
    {
      color: anomaliesChartColors.bar,
      label: "No Cost Anomaly",
    },
    {
      color: anomaliesChartColors.barWithAnomaly,
      label: "Anomaly Cost detected",
    },
  ];

  const thresholdData = [
    {
      color: anomaliesChartColors.threshold,
      label: "Anomaly Cost Threshold",
    },
  ];

  const data =
    granularity === Granularity.MONTHS
      ? costData
      : [...costData, ...thresholdData];

  return <Legend inline data={data} />;
}

function xScaleValue(value?: number) {
  return value ?? 0;
}

type TooltipArguments = {
  tooltipLeft: number;
  tooltipTop: number;
  tooltipData: JSX.Element;
};
